// [T155P3] Data Health Hub — parent container
// Aggregates 3 trust signals: Categorization, Account Freshness, Privacy Shares.
// Accepts optional `compact` prop for Dashboard mobile chip mode.
import { useEffect, useMemo } from 'react';
import { ShieldCheck, AlertTriangle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchReviewQueue } from '@/features/transactions/transactionsSlice';
import CategorizationHealth from './CategorizationHealth';
import AccountFreshnessHealth from './AccountFreshnessHealth';
import PrivacySharesHealth from './PrivacySharesHealth';

type StatusLevel = 'green' | 'amber' | 'red';

const STATUS_RANK: Record<StatusLevel, number> = { green: 0, amber: 1, red: 2 };

function worstStatus(...statuses: StatusLevel[]): StatusLevel {
  return statuses.reduce(
    (worst, s) => (STATUS_RANK[s] > STATUS_RANK[worst] ? s : worst),
    'green' as StatusLevel
  );
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

interface Props {
  compact?: boolean;
}

const statusConfig = {
  green: {
    Icon: ShieldCheck,
    label: 'Healthy',
    badgeClass:
      'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400',
  },
  amber: {
    Icon: AlertTriangle,
    label: 'Needs Attention',
    badgeClass:
      'bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
  },
  red: {
    Icon: AlertCircle,
    label: 'Action Required',
    badgeClass: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  },
};

export default function DataHealthPanel({ compact = false }: Props) {
  const dispatch = useAppDispatch();
  const reviewQueue = useAppSelector((state) => state.transactions.reviewQueue);
  const accounts = useAppSelector((state) => state.accounts.accounts);

  useEffect(() => {
    if (!reviewQueue) {
      dispatch(fetchReviewQueue());
    }
  }, [dispatch, reviewQueue]);

  const categorizationStatus = useMemo((): StatusLevel => {
    if (!reviewQueue || reviewQueue.total === 0) return 'green';
    const categorized = reviewQueue.total - reviewQueue.uncategorized.count;
    const pct = (categorized / reviewQueue.total) * 100;
    if (pct >= 80) return 'green';
    if (pct >= 50) return 'amber';
    return 'red';
  }, [reviewQueue]);

  const accountFreshnessStatus = useMemo((): StatusLevel => {
    const now = Date.now();
    const staleCount = accounts.filter(
      (a) => now - new Date(a.updatedAt).getTime() > THIRTY_DAYS_MS
    ).length;
    if (staleCount === 0) return 'green';
    if (staleCount <= 2) return 'amber';
    return 'red';
  }, [accounts]);

  const overallStatus = worstStatus(
    categorizationStatus,
    accountFreshnessStatus,
    'green' // PrivacySharesHealth always green (T134-F1 gated)
  );

  const { Icon: StatusIcon, label: statusLabel, badgeClass } =
    statusConfig[overallStatus];

  const statusBadge = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${badgeClass}`}
    >
      <StatusIcon className="h-3.5 w-3.5" />
      {statusLabel}
    </span>
  );

  if (compact) {
    return (
      <div data-testid="data-health-panel" className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Data Health</span>
        {statusBadge}
      </div>
    );
  }

  return (
    <Card data-testid="data-health-panel" className="glass-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base font-semibold">Data Health</CardTitle>
        {statusBadge}
      </CardHeader>
      <CardContent className="space-y-4">
        <CategorizationHealth
          reviewQueue={reviewQueue}
          status={categorizationStatus}
        />
        <AccountFreshnessHealth
          accounts={accounts}
          status={accountFreshnessStatus}
        />
        <PrivacySharesHealth />
      </CardContent>
    </Card>
  );
}
